import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';

import { getUser } from '../services/api';

interface UserContextType {
  userDetails: IUserDetails | null;
}

interface IUserDetails {
  firstName: string;
  lastName: string;
}

interface IProps {
  children: React.ReactNode;
}

const UserContext = React.createContext<UserContextType | null>(null);

export const UserProvider: React.FC<IProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const { pathname } = useLocation();

  const [userDetails, setUserDetails] = React.useState<IUserDetails>({
    firstName: "",
    lastName: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["getUserDetails"],
    queryFn: getUser,
    enabled:
      inProgress === "none" &&
      pathname !== "/home" &&
      !!accounts &&
      accounts.length >= 1,
  });

  React.useEffect(() => {
    if (!!data && !isLoading) {
      const parts = data.name.trim().split(" ");
      const firstName = parts[0];
      const lastName = parts.slice(1).join(" ");

      setUserDetails({ firstName, lastName });
    }
  }, [data, isLoading]);

  return (
    <UserContext.Provider
      value={{
        userDetails,
      }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
